// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/HomeBefore.tsx
// 🔧 PURPOSE: La vista B della Home: prima dell'arrivo, e il giorno
//             dell'arrivo fino alle 15:00 di Roma (la variante "OGGI"; dalle
//             15:00 la Home passa al soggiorno).
//
//             Props (HomeBeforeProps = HomeBookedViewProps, components/home/types):
//             - state: HomeState (view 'before': phase far / soon / arrival,
//               daysToCheckIn, isArrivalDay, checkInDone, checkInPath,
//               checkInWindowOpen, checkInOpensOn, taxDue, doorCodeAvailable,
//               transferBooked, isGroup...);
//             - now: l'ora del clock della Home (useNow);
//             - token: il token della sessione;
//             - booking: la prenotazione (nome, appartamento, ospiti, email,
//               indirizzo, tassa, extra comprati...);
//             - guestName: il primo nome, o null;
//             - catalog: il catalogo degli extra (null finché non arriva;
//               controllare catalog.success).
//             Login, chat e assistente da useHomeSession(); finestre dei
//             servizi da useServiceDialogs().
//
//             Ordine (mockup Arrivo + decisioni): intestazione "TI
//             ASPETTIAMO" (con la foto dell'appartamento prenotato,
//             booking.apartmentPhotoUrl) + StayCard con QuickActions; Da
//             fare (check-in o codice pronto, tassa); Adesso per te (solo
//             il check-in anticipato, il giorno prima dell'arrivo e quello
//             dell'arrivo: NowForYou; allora la tessera di "Prepara
//             l'arrivo" non lo ripete); Prepara l'arrivo
//             (Cestino della Nonna con il prezzo barrato, early check-in,
//             taxi, late check-out);
//             Nelle tue date; Buono a sapersi (UsefulServices 'before');
//             Scopri (DiscoverRail 'before'); Offerte (SpecialOffers
//             'before'); Domande sull'arrivo? (ReceptionCard 'ask', che apre
//             la scelta Assistente AI / reception); Intrattenimento; footer.
//             Niente "Arrivi in aereo? Aggiungi il volo": tolto (D8, A).
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { HomeBookedViewProps } from './types';
import HomeMasthead from './HomeMasthead';
import StayCard from './StayCard';
import QuickActions, { defaultQuickActions } from './QuickActions';
import CheckInTodo from './CheckInTodo';
import PrepareArrival from './PrepareArrival';
import NowForYou from './NowForYou';
import { earlyCheckinOffer } from '../../lib/earlyCheckin';
import EventsInDates from './EventsInDates';
import UsefulServices from './UsefulServices';
import DiscoverRail from './DiscoverRail';
import ReceptionCard from './ReceptionCard';
import Entertainment from './Entertainment';
import HomeFooter from './HomeFooter';
import SpecialOffers from '../SpecialOffers';

export type HomeBeforeProps = HomeBookedViewProps;

const HomeBefore: React.FC<HomeBeforeProps> = ({ state, now, token, booking, guestName, catalog }) => {
  const { t } = useTranslation();
  const guests = booking.numberOfGuests > 0 ? t('home.stay.guests', { count: booking.numberOfGuests }) : '';
  // Per un gruppo i nomi degli appartamenti sono tanti: si tagliano, gli
  // ospiti restano sempre in vista.
  const sub = booking.apartmentName || guests ? (
    <span className="flex min-w-0">
      {booking.apartmentName ? <span className="min-w-0 truncate">{booking.apartmentName}</span> : null}
      {booking.apartmentName && guests ? <span className="flex-shrink-0 whitespace-pre"> · </span> : null}
      {guests ? <span className="flex-shrink-0 whitespace-nowrap">{guests}</span> : null}
    </span>
  ) : undefined;

  // Le tessere le decide lo stato; il giorno dell'arrivo, a check-in fatto,
  // fra loro c'è il codice della porta e "Da fare" non lo ripete.
  const quickItems = defaultQuickActions(state, booking);

  // Il check-in anticipato proposto "adesso" (giorno prima / giorno
  // dell'arrivo): la card sta in "Adesso per te", e "Prepara l'arrivo" non
  // ripete la stessa tessera.
  const earlyNow = !!earlyCheckinOffer({ catalog, booking, now, houseCheckInTime: state.houseCheckInTime });

  return (
    <>
      <HomeMasthead
        eyebrow={t('home.masthead.waiting')}
        title={guestName ? t('home.masthead.hello', { name: guestName }) : t('home.masthead.helloNoName')}
        sub={sub}
        photoUrl={booking.apartmentPhotoUrl}
      />
      <StayCard state={state} variant="before">
        <QuickActions state={state} token={token} booking={booking} items={quickItems} />
      </StayCard>
      <CheckInTodo state={state} token={token} booking={booking} doorInQuickActions={quickItems.indexOf('door') >= 0} />
      {earlyNow ? <NowForYou state={state} now={now} booking={booking} catalog={catalog} /> : null}
      <PrepareArrival state={state} catalog={catalog} hideEarlyCheckin={earlyNow} />
      <EventsInDates state={state} now={now} />
      <UsefulServices variant="before" />
      <DiscoverRail variant="before" />
      <SpecialOffers variant="before" />
      <ReceptionCard variant="ask" />
      <Entertainment />
      <HomeFooter />
    </>
  );
};

export default HomeBefore;
